// PersonaAssessmentFlow.jsx - Multi-persona assessment interface for Foundations
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Users, User, CheckCircle, Clock, AlertTriangle, ChevronRight,
  ChevronLeft, ArrowLeft, Save, Send, RefreshCw, Settings, Eye, UserCheck,
  Building, Shield, Scale, Crown, Target, FileText, Database,
  Globe, Award, Activity, Plus, Minus, BarChart3, Info
} from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button, PrimaryButton, SecondaryButton } from '../../../components/ui/Button';
import AssessmentPausedNotice from '../components/AssessmentPausedNotice';
import { isAssessmentPaused } from '../components/assessmentPaused';
import PolicyEvaluationPage from '../../../components/PolicyEvaluationPage';
import PersonaSetup from '../../../components/PersonaSetup';
import { Badge, StatusBadge } from '../../../components/ui/Badge';
import apiService from '../../../services/api';
import apiConfig from '../../../config/apiConfig';
import { authHeaders } from '../../../services/authHeaders';
import '../../../styles/insights.css';
import HowItWorksGuide from '../../../shared/components/HowItWorksGuide';
import HelpTooltip from '../../../shared/components/HelpTooltip';
import { getCategoryHelp } from '../../../data/questionCategoryHelp';
import { notifyNextOutcome } from './notifyNextOutcome';
// Removed hardcoded questions import - now using database API

// All questions now use correct database IDs (1-51)
// No mapping needed since database was restored with correct numbering

// Persona configuration based on Excel columns D, E, F, G
const PERSONA_CONFIG = {
  c_level_tech: {
    id: 'c_level_tech',
    name: 'C-Level Technology',
    description: 'CTO, CIO, Chief Technology Officer',
    icon: <Building aria-hidden="true" className="w-5 h-5" />,
    color: 'blue',
    excelColumn: 'D'
  },
  c_level_hr: {
    id: 'c_level_hr',
    name: 'Human Resources',
    description: 'HR Director, People Operations',
    icon: <Users aria-hidden="true" className="w-5 h-5" />,
    color: 'green',
    excelColumn: 'E'
  },
  legal: {
    id: 'legal',
    name: 'Legal & Compliance',
    description: 'General Counsel, Compliance Officer',
    icon: <Scale className="w-5 h-5" />,
    color: 'purple',
    excelColumn: 'F'
  },
  ceo_board: {
    id: 'ceo_board',
    name: 'CEO/Board Level', 
    description: 'CEO, Board Members, Executive Leadership',
    icon: <Crown className="w-5 h-5" />,
    color: 'gold',
    excelColumn: 'G'
  }
};

const PersonaAssessmentFlow = ({ sessionId: propSessionId, companyId, onComplete, onBack }) => {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('overview');
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [paused, setPaused] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [sessionId, setSessionId] = useState(
    propSessionId || (urlSessionId && urlSessionId !== 'new' ? urlSessionId : null)
  );
  const [assessmentSession, setAssessmentSession] = useState(null);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [showPolicyEvaluation, setShowPolicyEvaluation] = useState(false);
  const [scoringGuideVisible, setScoringGuideVisible] = useState({});

  // Persona OTP Phase 3 — when arriving via magic link the URL carries
  // ?persona=:type&locked=1; lockedPersona pins the UI to a single persona
  // section, removes the multi-persona switcher, and turns end-of-flow paths
  // into a thank-you screen instead of a Back-to-Overview view.
  const [lockedPersona, setLockedPersona] = useState(() => {
    if (typeof window === 'undefined') return null;
    const sp = new URLSearchParams(window.location.search);
    return sp.get('locked') === '1' ? sp.get('persona') : null;
  });

  const signOutPersona = useCallback(() => {
    try {
      localStorage.removeItem('foundations_persona_token');
      localStorage.removeItem('foundations_persona_session');
    } catch { /* ignore */ }
    navigate('/login', { replace: true });
  }, [navigate]);

  // Extract URL parameters for pre-filling
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const companyParam = urlParams.get('company');
    const industryParam = urlParams.get('industry');
    const personaParam = urlParams.get('persona');
    
    if (companyParam || industryParam) {
      console.log('🏢 URL params found - Company:', companyParam, 'Industry:', industryParam);
      setCompanyInfo({
        name: companyParam || '',
        industry: industryParam || ''
      });
      
      // Pre-fill responses for questions 1 and 2 - will be updated when questions are loaded
      // Temporarily store the URL parameters for later use
      window.urlCompanyParam = companyParam;
      window.urlIndustryParam = industryParam;
    }
    
    // If persona parameter is present, set selected persona and switch to assessment
    if (personaParam) {
      console.log('👤 Persona parameter found:', personaParam);
      setSelectedPersona(personaParam);
      setCurrentStep('assessment');
    }
  }, []);

  // Load assessment data and questions
  useEffect(() => {
    console.log('🔍 Debug - urlSessionId:', urlSessionId, 'sessionId:', sessionId);
    console.log('🔍 Debug - propSessionId:', propSessionId);
    
    // If URL contains "new" or sessionId is null/empty, create new session
    if (urlSessionId === 'new' || !sessionId) {
      console.log('🚀 Creating new session (URL is "new" or sessionId is null)');
      createNewSession();
    } else {
      console.log('📂 Loading existing session:', sessionId);
      loadExistingSession();
    }
  }, []);

  // Cleanup auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [autoSaveTimeout]);

  const createNewSession = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🚀 Creating new assessment session...');

      // Get user info from localStorage (set during login)
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      
      // Create assessment session with authenticated coordinator data
      const sessionResponse = await apiService.post('/api/foundations/sessions', {
        company_name: userInfo.company_name || 'Assessment Company',
        user_email: userInfo.email || 'user@company.com',
        coordinator_company_id: userInfo.company_id
      });

      if (sessionResponse.success) {
        const newSessionId = sessionResponse.session.id;
        setSessionId(newSessionId);
        setAssessmentSession(sessionResponse.session);
        console.log('✅ Created session:', newSessionId);
        
        // Store session ID for cross-app navigation
        localStorage.setItem('currentSessionId', newSessionId);
        localStorage.setItem('activeSessionId', newSessionId);
        
        // Now load questions
        await loadAssessmentData();
      } else {
        throw new Error('Failed to create assessment session');
      }

    } catch (error) {
      if (isAssessmentPaused(error)) { setPaused(true); setLoading(false); return; }
      console.error('❌ Error creating session:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const loadExistingSession = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Loading existing session:', sessionId);

      // Load session status
      const sessionResponse = await apiService.get(`/api/foundations/sessions/${sessionId}`);
      
      if (sessionResponse.success) {
        const sessionData = {
          ...sessionResponse.session,
          persona_sessions: sessionResponse.persona_sessions || []
        };
        setAssessmentSession(sessionData);
        console.log('✅ Loaded existing session with persona sessions:', sessionResponse.persona_sessions?.length || 0);
        
        // Store session ID for cross-app navigation
        localStorage.setItem('currentSessionId', sessionId);
        localStorage.setItem('activeSessionId', sessionId);

        // Load company information
        await loadCompanyInfo();
        
        // Load questions first (without initializing progress)
        await loadQuestionsOnly();
        // Then load existing responses which will set the correct progress
        await loadExistingResponses(sessionData);
        
        // Check if C-Level Tech is complete and should show policy evaluation
        const cLevelTechSession = sessionData.persona_sessions?.find(ps => ps.persona_code === 'c_level_tech');
        if (cLevelTechSession && cLevelTechSession.completion_percentage >= 100) {
          console.log('🎯 C-Level Tech already completed, checking if should show policy evaluation');
          // Show policy evaluation if we're not on a specific persona assessment OR if we are specifically on c_level_tech
          const urlParams = new URLSearchParams(window.location.search);
          const currentPersona = urlParams.get('persona');
          const skipPolicy = urlParams.get('skipPolicy'); // Allow bypassing policy evaluation
          if ((!currentPersona || currentPersona === 'c_level_tech') && !skipPolicy) {
            setShowPolicyEvaluation(true);
            console.log('📋 Showing policy evaluation page for completed C-Level Tech');
          } else if (skipPolicy) {
            console.log('⏭️ Skipping policy evaluation due to skipPolicy parameter');
          }
        }
      } else {
        throw new Error('Failed to load existing session');
      }

    } catch (error) {
      if (isAssessmentPaused(error)) { setPaused(true); setLoading(false); return; }
      console.error('❌ Error loading existing session:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const loadQuestionsOnly = async () => {
    try {
      console.log('📝 Loading questions from database API...');
      
      // Load questions from database API (not hardcoded file)
      const questionsResponse = await apiService.get('/api/foundations/questions');
      
      if (!questionsResponse.success) {
        throw new Error('Failed to load questions from database');
      }
      
      console.log('✅ Loaded questions from database:', questionsResponse.questions?.length || 0);
      
      // Transform database questions to match frontend expectations
      const databaseQuestions = questionsResponse.questions.map(q => {
        // Parse JSON fields if they're strings
        let options = null;
        let descriptions = null;
        
        try {
          if (q.options && typeof q.options === 'string') {
            options = JSON.parse(q.options);
          } else {
            options = q.options;
          }
        } catch (e) {
          console.warn('Could not parse options for question', q.id, ':', q.options);
        }
        
        try {
          if (q.descriptions && typeof q.descriptions === 'string') {
            descriptions = JSON.parse(q.descriptions);
          } else {
            descriptions = q.descriptions;
          }
        } catch (e) {
          console.warn('Could not parse descriptions for question', q.id, ':', q.descriptions);
        }
        
        return {
          id: q.id, // Keep database integer ID
          question_text: q.question_text,
          question_type: q.question_type,
          category: q.category,
          options: options,
          scale: q.scale,
          descriptions: descriptions,
          placeholder: q.placeholder || null,
          c_level_tech: q.c_level_tech || false,
          c_level_hr: q.c_level_hr || false,
          legal: q.legal || false,
          ceo_board: q.ceo_board || false
        };
      });
      
      setQuestions(databaseQuestions);
      console.log(`📋 Loaded ${databaseQuestions.length} database questions with scoring`);
      
      // Debug cyber insurance questions
      const cyberInsuranceQuestions = databaseQuestions.filter(q => q.category === 'cyber_insurance');
      console.log('🔍 DEBUG Cyber Insurance questions loaded:', cyberInsuranceQuestions.length, cyberInsuranceQuestions.map(q => ({id: q.id, text: q.question_text.substring(0, 50)})));
      
      // Debug 360 questions (now IDs 17-48 in database)
      const e360Questions = databaseQuestions.filter(q => q.category === 'e360');
      console.log('🔍 DEBUG e360 questions in main loaded questions:', e360Questions.length, e360Questions[0]);
      
      // Pre-fill company and industry data from URL parameters
      if (window.urlCompanyParam || window.urlIndustryParam) {
        setTimeout(() => {
          preFillFromUrlParams(databaseQuestions);
        }, 100);
      }
      
      // Auto-fill company name if we have company info
      if (companyInfo?.name) {
        autoFillCompanyName(companyInfo.name);
      }

    } catch (error) {
      if (isAssessmentPaused(error)) { setPaused(true); setLoading(false); return; }
      console.error('❌ Error loading questions:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAssessmentData = async () => {
    try {
      console.log('🔄 Loading Foundations assessment questions with Excel column assignments');

      // Load questions for assessment (force fresh load)
      const questionsResponse = await apiService.get('/api/foundations/questions?t=' + Date.now());
      
      console.log('✅ API Response:', questionsResponse);

      if (questionsResponse.success && questionsResponse.questions) {
        // Filter questions that have persona assignments (Excel columns)
        const questionsWithPersonas = questionsResponse.questions.filter(q => 
          q.c_level_tech || q.c_level_hr || q.legal || q.ceo_board
        );
        setQuestions(questionsWithPersonas);
        console.log('✅ Persona questions loaded:', questionsWithPersonas.length);
        console.log('📊 Persona counts:', questionsResponse.persona_counts);
        
        // Debug: Check if 360 questions have proper scoring guides
        const e360Questions = questionsResponse.questions.filter(q => q.category === 'e360');
        console.log('🔍 E360 questions loaded:', e360Questions.length);
        
        const questionsWithScoring = questionsWithPersonas.filter(q => q.descriptions && q.scale);
        console.log('🎯 Questions with scoring guides:', questionsWithScoring.length);
        
        // Initialize progress tracking (only when creating new session)
        initializeProgress(questionsWithPersonas);
        
        // Auto-fill company name if we have company info
        if (companyInfo?.name) {
          autoFillCompanyName(companyInfo.name);
        }
      } else {
        throw new Error('Failed to load questions from API');
      }

    } catch (error) {
      if (isAssessmentPaused(error)) { setPaused(true); setLoading(false); return; }
      console.error('❌ Error loading assessment data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const initializeProgress = (questionsData) => {
    const progressData = {};
    
    // Initialize with default values - actual progress will come from backend
    Object.keys(PERSONA_CONFIG).forEach(personaId => {
      progressData[personaId] = {
        total: 0, // Will be set by backend data
        completed: 0,
        percentage: 0
      };
    });
    
    setProgress(progressData);
    console.log('🔧 Initialized empty progress - will be populated by backend data');
  };

  const loadCompanyInfo = async () => {
    try {
      console.log('🏢 Loading company information for session:', sessionId);
      
      // Try to get company profile from the company-profiles endpoint
      const API_BASE = apiConfig.getBaseUrl();
      const response = await fetch(`${API_BASE}/api/assessments/sessions/${sessionId}/company-profile`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.company_profile) {
          const companyName = data.company_profile.company_name;
          setCompanyInfo({
            name: companyName,
            industry: data.company_profile.industry_vertical
          });
          console.log('✅ Loaded company info:', companyName);
          
          // Auto-fill Question 1 with company name if questions are loaded
          if (companyName && questions.length > 0) {
            setTimeout(() => autoFillCompanyName(companyName), 100);
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not load company info:', error);
    }
  };

  const preFillFromUrlParamsWithPersona = (personaId) => {
    console.log('🔧 Pre-filling questions from URL parameters for persona:', personaId);
    
    if (!questions || questions.length === 0) {
      console.warn('⚠️ No questions loaded yet for pre-fill');
      return;
    }
    
    // Find questions 1 and 2 (company name and industry)
    const companyQuestion = questions.find(q => 
      q.question_text && (
        q.question_text.toLowerCase().includes('company name') ||
        q.question_text.toLowerCase().includes('what is your company')
      )
    );
    
    const industryQuestion = questions.find(q => 
      q.question_text && (
        q.question_text.toLowerCase().includes('industry vertical') ||
        q.question_text.toLowerCase().includes('industry')
      )
    );
    
    console.log('🔍 Found company question:', companyQuestion?.question_text);
    console.log('🔍 Found industry question:', industryQuestion?.question_text);
    
    const updates = {};
    
    if (companyQuestion && window.urlCompanyParam) {
      const responseKey = `${companyQuestion.id}_${personaId}`;
      updates[responseKey] = {
        question_id: companyQuestion.id,
        persona: personaId,
        response_value: window.urlCompanyParam,
        auto_filled: true
      };
      console.log('✅ Pre-filled company question:', responseKey, '=', window.urlCompanyParam);
    }
    
    if (industryQuestion && window.urlIndustryParam) {
      const responseKey = `${industryQuestion.id}_${personaId}`;
      updates[responseKey] = {
        question_id: industryQuestion.id,
        persona: personaId,
        response_value: window.urlIndustryParam,
        auto_filled: true
      };
      console.log('✅ Pre-filled industry question:', responseKey, '=', window.urlIndustryParam);
    }
    
    if (Object.keys(updates).length > 0) {
      console.log('🔧 Applying pre-fill updates:', updates);
      setResponses(prev => {
        // Only update if the value is empty or different
        const merged = { ...prev };
        Object.keys(updates).forEach(key => {
          if (!merged[key] || !merged[key].response_value || merged[key].response_value === '') {
            console.log('✅ Setting pre-fill for:', key);
            merged[key] = updates[key];
          } else {
            console.log('⚠️ Skipping pre-fill for', key, '- already has value:', merged[key].response_value);
          }
        });
        return merged;
      });
    } else {
      console.warn('⚠️ No pre-fill updates to apply');
    }
  };

  const preFillFromUrlParams = (questionsData) => {
    console.log('🔧 Pre-filling questions from URL parameters (legacy)');
    
    if (!selectedPersona) {
      console.warn('⚠️ No selected persona for pre-fill');
      return;
    }
    
    // Use the new function with the current selectedPersona
    preFillFromUrlParamsWithPersona(selectedPersona);
  };

  const autoFillCompanyName = (companyName) => {
    // Find Question 1 or any question asking for company name
    const companyQuestion = questions.find(q => 
      q.question_number === 1 || 
      q.question_text.toLowerCase().includes('company name') ||
      q.question_text.toLowerCase().includes('what is your company')
    );
    
    if (companyQuestion) {
      Object.keys(PERSONA_CONFIG).forEach(personaId => {
        const responseKey = `${companyQuestion.id}_${personaId}`;
        // Auto-fill if not already answered OR if it's empty
        const existingResponse = responses[responseKey];
        const isEmpty = !existingResponse || !existingResponse.response_value || existingResponse.response_value.trim() === '';
        
        if (isEmpty) {
          setResponses(prev => ({
            ...prev,
            [responseKey]: {
              question_id: companyQuestion.id,
              persona: personaId,
              response_value: companyName,
              auto_filled: true
            }
          }));
        }
      });
      console.log('✅ Auto-filled company name for Question 1:', companyName);
    }
  };

  const toggleScoringGuide = (questionId) => {
    setScoringGuideVisible(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const loadExistingResponses = async (sessionData = null) => {
    try {
      console.log('📋 Loading existing responses for session:', sessionId);
      
      // Use passed sessionData or fall back to state
      const session = sessionData || assessmentSession;
      
      if (session && session.persona_sessions) {
        console.log('🔧 Processing persona sessions from backend:', session.persona_sessions);
        
        // Build complete progress data from backend
        const backendProgress = {};
        
        session.persona_sessions.forEach(ps => {
          console.log('🔧 Processing backend persona session:', ps.persona_code, {
            answered: ps.questions_answered,
            total: ps.questions_total, 
            percentage: ps.completion_percentage,
            status: ps.status
          });
          
          const personaId = ps.persona_code;
          
          // Account for coordinator questions (1 and 2) that may be included in the count
          // These are answered by coordinator but counted in persona responses
          const coordinatorQuestions = 2; // Questions 1 and 2
          const adjustedAnswered = Math.max(0, parseInt(ps.questions_answered || 0) - coordinatorQuestions);
          const adjustedTotal = parseInt(ps.questions_total || 0);
          
          // Always use backend data as the source of truth, but adjust for coordinator questions
          backendProgress[personaId] = {
            total: adjustedTotal,
            completed: Math.min(adjustedAnswered, adjustedTotal), // Cap at total to avoid > 100%
            percentage: adjustedTotal > 0 ? Math.round((Math.min(adjustedAnswered, adjustedTotal) / adjustedTotal) * 100) : 0
          };
          
          console.log('🔧 Set progress for', personaId, ':', backendProgress[personaId]);
        });
        
        // Ensure we have all personas initialized
        Object.keys(PERSONA_CONFIG).forEach(personaId => {
          if (!backendProgress[personaId]) {
            backendProgress[personaId] = {
              total: 0,
              completed: 0, 
              percentage: 0
            };
            console.log('🔧 Initialized missing persona:', personaId);
          }
        });
        
        console.log('🔧 Final backend progress being set:', backendProgress);
        
        // Set the complete progress data from backend
        setProgress(backendProgress);
        
        // Load actual response data for resuming assessments
        await loadResponseData();
      } else {
        console.warn('🔧 No assessment session or persona sessions found');
        // Initialize empty progress
        const emptyProgress = {};
        Object.keys(PERSONA_CONFIG).forEach(personaId => {
          emptyProgress[personaId] = { total: 0, completed: 0, percentage: 0 };
        });
        setProgress(emptyProgress);
      }
    } catch (error) {
      console.warn('Error loading existing responses:', error.message);
    }
  };

  const loadResponseData = async () => {
    try {
      console.log('📋 Loading existing response data for resume functionality...');
      
      // Get all responses for this session
      const API_BASE = apiConfig.getBaseUrl();
      const responsesResponse = await fetch(`${API_BASE}/api/foundations/sessions/${sessionId}/responses`, { headers: authHeaders() });
      
      if (responsesResponse.ok) {
        const responseData = await responsesResponse.json();
        console.log('📥 Response data from backend:', responseData);
        if (responseData.success && responseData.responses && responseData.responses.length > 0) {
          const loadedResponses = {};
          
          responseData.responses.forEach(resp => {
            // Convert response format to match frontend format
            const responseKey = `${resp.question_id}_${resp.persona_code}`;
            let responseValue;
            
            try {
              // Parse the answer JSON if it's a string
              if (typeof resp.answer === 'string') {
                const parsed = JSON.parse(resp.answer);
                responseValue = parsed.value !== undefined ? parsed.value : parsed;
              } else if (resp.answer && typeof resp.answer === 'object') {
                responseValue = resp.answer.value !== undefined ? resp.answer.value : resp.answer;
              } else {
                responseValue = resp.answer;
              }
            } catch (e) {
              console.warn('⚠️ Failed to parse answer:', resp.answer, e);
              responseValue = resp.answer;
            }
            
            console.log(`📝 Loading response ${responseKey}:`, responseValue);
            
            loadedResponses[responseKey] = {
              question_id: resp.question_id,
              persona: resp.persona_code,
              response_value: responseValue,
              loaded_from_db: true
            };
          });
          
          console.log('✅ Loaded existing responses:', Object.keys(loadedResponses).length, loadedResponses);
          
          // Smart merge: preserve user input, only use database for missing responses
          setResponses(prev => {
            console.log('🔧 Smart merging responses - Previous:', Object.keys(prev).length, 'New:', Object.keys(loadedResponses).length);
            
            // Start with previous responses to preserve user input
            const merged = { ...prev };
            
            // Only add database responses if there's no existing user input
            Object.keys(loadedResponses).forEach(key => {
              const currentResponse = merged[key];
              const dbResponse = loadedResponses[key];
              
              // Check if current response has meaningful content
              const hasUserInput = currentResponse && 
                currentResponse.response_value && 
                currentResponse.response_value.toString().trim() !== '';
              
              // Check if current response is recent (within 10 seconds)
              const hasRecentUserInput = currentResponse && 
                currentResponse.timestamp && 
                (Date.now() - new Date(currentResponse.timestamp).getTime()) < 10000;
              
              // Skip empty database values to preserve user input
              const dbValueIsEmpty = !dbResponse?.response_value || dbResponse.response_value.toString().trim() === '';
              
              if (dbValueIsEmpty && hasUserInput) {
                // Skip empty database values when user has input
                console.log(`🔍 Preserving user input for Q${dbResponse.question_id}`);
                // Don't overwrite with empty database value
              } else if (!hasUserInput && !hasRecentUserInput) {
                // No user input or stale data, use database value
                merged[key] = dbResponse;
              } else if ((dbResponse?.question_id >= 146 && dbResponse?.question_id <= 148) || 
                        (dbResponse?.question_id >= 17 && dbResponse?.question_id <= 24)) {
                // Debug log for our problem questions
                console.log(`🔍 Preserving user input for Q${dbResponse.question_id}:`, currentResponse?.response_value, 'over database:', dbResponse?.response_value);
              }
            });
            
            console.log('✅ Smart merged responses total:', Object.keys(merged).length);
            return merged;
          });
        }
      } else {
        console.log('📋 No existing responses found or endpoint not available');
      }
    } catch (error) {
      console.warn('Error loading response data:', error.message);
    }
  };

  // Removed updateProgressFromResponses function - progress is now handled entirely by the backend

  // Helper function to get the response value with pre-fill fallback
  const getResponseValue = (questionId, personaId) => {
    const responseKey = `${questionId}_${personaId}`;
    const existingResponse = responses[responseKey];
    
    // If we have a saved response, return it
    if (existingResponse?.response_value) {
      return existingResponse.response_value;
    }
    
    // Otherwise, check if this is a company or industry question and use URL params
    const question = questions.find(q => q.id === questionId);
    if (question) {
      // Check for company name question
      if (question.question_text && (
        question.question_text.toLowerCase().includes('company name') ||
        question.question_text.toLowerCase().includes('what is your company')
      )) {
        const companyValue = window.urlCompanyParam || companyInfo?.name || '';
        console.log('📝 Using pre-fill for company question:', questionId, '=', companyValue);
        return companyValue;
      }
      
      // Check for industry question  
      if (question.question_text && (
        question.question_text.toLowerCase().includes('industry vertical') ||
        question.question_text.toLowerCase().includes('industry')
      )) {
        const industryValue = window.urlIndustryParam || companyInfo?.industry || '';
        console.log('📝 Using pre-fill for industry question:', questionId, '=', industryValue);
        return industryValue;
      }
    }
    
    return '';
  };

  const getQuestionsForPersona = useCallback((personaId) => {
    // Use the persona ID directly to match database column names
    return questions.filter(q => {
      return q[personaId]; // Direct mapping: c_level_tech, c_level_hr, legal, ceo_board
    });
  }, [questions]);

  const handleResponseChange = (questionId, value, persona) => {
    const responseKey = `${questionId}_${persona}`;
    
    setResponses(prev => {
      const newResponse = {
        question_id: questionId,
        persona: persona,
        response_value: value,
        timestamp: new Date().toISOString()
      };
      
      return {
        ...prev,
        [responseKey]: newResponse
      };
    });

    // Auto-save after a short delay
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    const newTimeout = setTimeout(() => {
      saveResponses(persona);
    }, 2000); // Save 2 seconds after last change
    
    setAutoSaveTimeout(newTimeout);
  };

  const saveResponses = async (personaId = selectedPersona) => {
    if (!sessionId || !personaId) {
      console.warn('⚠️ Cannot save: missing sessionId or personaId');
      return;
    }

    try {
      setSaving(true);
      console.log('💾 Saving responses for persona:', personaId);

      // Filter responses for the current persona
      const personaResponses = Object.values(responses).filter(r => r.persona === personaId);
      
      // Debug 360 questions in save
      const e360_responses = personaResponses.filter(r => {
        return questions.find(q => q.id === r.question_id && q.category === 'e360');
      });
      const q146_148_responses = e360_responses;
      if (q146_148_responses.length > 0) {
        console.log(`🔍 DEBUG SAVE: Found ${q146_148_responses.length} responses for e360 questions:`, q146_148_responses);
      }
      
      if (personaResponses.length === 0) {
        console.log('📝 No responses to save for persona:', personaId);
        return;
      }

      // Convert persona ID to persona code for API
      const personaCode = personaId; // The personaId is already the correct code

      const saveRequest = {
        responses: personaResponses,
        persona_code: personaCode
      };

      console.log('📤 Saving request:', saveRequest);

      const result = await apiService.post(`/api/foundations/sessions/${sessionId}/responses`, saveRequest);
      
      if (result.success) {
        console.log('✅ Responses saved successfully:', result.message);
        // Don't reload responses after save - this overwrites user input!
        // Just refresh the progress data without overwriting responses
        setTimeout(async () => {
          try {
            const sessionResponse = await apiService.get(`/api/foundations/sessions/${sessionId}`);
            if (sessionResponse.success) {
              // Only update progress, not responses
              const backendProgress = {};
              if (sessionResponse.persona_sessions) {
                sessionResponse.persona_sessions.forEach(ps => {
                  const progressKey = ps.persona_code;
                  backendProgress[progressKey] = {
                    total: ps.questions_total || 0,
                    completed: ps.questions_answered || 0,
                    percentage: Math.round(ps.completion_percentage || 0)
                  };
                });
                setProgress(backendProgress);
                console.log('✅ Progress updated after save without overwriting responses');
              }
            }
          } catch (error) {
            console.warn('Failed to refresh progress after save:', error);
          }
        }, 100); // Small delay to ensure backend has processed
      } else {
        throw new Error(result.error || 'Failed to save responses');
      }
      
    } catch (error) {
      if (isAssessmentPaused(error)) { setPaused(true); setLoading(false); return; }
      console.error('❌ Error saving responses:', error);
      setError(`Failed to save responses: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const nextQuestion = () => {
    const personaQuestions = getQuestionsForPersona(selectedPersona);
    if (currentQuestionIndex < personaQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const completePersonaAssessment = async () => {
    try {
      // Save responses first
      console.log('💾 About to save responses for completion...');
      await saveResponses(selectedPersona);
      console.log('✅ Responses saved, proceeding to notification logic');
      
      // Notify next persona
      console.log('📧 Completing assessment for persona:', selectedPersona);
      
      try {
        console.log('🔍 Checking persona type:', selectedPersona);
        
        // Check if this is C-Level Tech completion - navigate to policy evaluation page
        if (selectedPersona === 'c_level_tech') {
          console.log('🎯 C-Level Tech completed - showing policy evaluation page');
          setShowPolicyEvaluation(true);
          return; // Don't proceed with normal flow, stay on policy evaluation page
        }

        console.log('📧 Non-C-Level Tech persona, proceeding with notification logic');
        
        // Send notification to next persona
        const personaNameMap = {
          'c_level_tech': 'C-Level Tech',
          'c_level_hr': 'C-Level HR', 
          'legal': 'Legal',
          'ceo_board': 'CEO/Board'
        };

        const completedPersonaName = personaNameMap[selectedPersona];
        console.log('🔍 About to send notification for:', completedPersonaName, 'from persona:', selectedPersona);
        
        try {
          console.log('📤 Making fetch request to notification API...');
          const API_BASE = apiConfig.getBaseUrl();
          const response = await fetch(`${API_BASE}/api/notifications/notify-next/${sessionId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ completedPersona: completedPersonaName })
          });

          console.log('📨 Response status:', response.status);
          
          // F20: a 200 can still carry success:false (e.g. nobody was invited to the session).
          const body = await response.json().catch(() => null);
          const outcome = notifyNextOutcome(response.ok, body);
          if (outcome.sent) {
            console.log('✅ Notification sent successfully:', body);
            console.log(`${PERSONA_CONFIG[selectedPersona].name} assessment completed! Notification sent to next persona.`);
          } else {
            console.warn('⚠️ Next persona was NOT notified:', outcome.reason, body);
            console.log(`${PERSONA_CONFIG[selectedPersona].name} assessment completed! Your responses have been saved.`);
          }
        } catch (notificationError) {
          console.error('❌ Notification service error:', notificationError);
          console.log(`${PERSONA_CONFIG[selectedPersona].name} assessment completed! Your responses have been saved.`);
        }
        
      } catch (notificationError) {
        console.warn('⚠️  Failed to send notification:', notificationError.message);
        console.error('Assessment complete! There was an issue sending notifications - please contact the administrator.');
      }
      
      // Show completion message with option to go to Risk Optimization Studio
      console.log('🎯 Assessment completed! Offering Risk Optimization Studio access...');
      
      // Store completion context for studio access
      const assessmentContext = {
        assessmentId: sessionId,
        persona: selectedPersona,
        personaDisplayName: PERSONA_CONFIG[selectedPersona]?.name || selectedPersona,
        completedAt: new Date().toISOString(),
        companyName: companyInfo?.name || 'Unknown Company',
        industry: companyInfo?.industry || 'Unknown Industry'
      };
      sessionStorage.setItem('assessmentContext', JSON.stringify(assessmentContext));
      sessionStorage.setItem('currentPersona', selectedPersona);
      
      // Assessment completed - no popup needed
      console.log('🎉 Assessment completed successfully for', PERSONA_CONFIG[selectedPersona].name);
      
      // If user chooses to stay, return to overview
      console.log('📊 User chose to stay in assessment overview');
      setCurrentStep('overview');
      setSelectedPersona(null);
      setCurrentQuestionIndex(0);
      
      // Clear persona parameter from URL
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.delete('persona');
      window.history.replaceState({}, '', currentUrl);
      
      // Refresh data to show updated progress - add longer delay to ensure backend is fully updated
      console.log('🔄 Refreshing session data to show updated progress after completion...');
      setTimeout(async () => {
        console.log('🔄 Starting delayed refresh after persona completion...');
        if (sessionId) {
          // Force a complete refresh of session data
          const sessionResponse = await apiService.get(`/api/foundations/sessions/${sessionId}`);
          if (sessionResponse.success) {
            const freshSessionData = {
              ...sessionResponse.session,
              persona_sessions: sessionResponse.persona_sessions || []
            };
            setAssessmentSession(freshSessionData);
            console.log('🔄 Loaded fresh session data, updating progress...');
            await loadExistingResponses(freshSessionData);
            console.log('✅ Progress refresh complete');
          }
        } else {
          await loadAssessmentData();
        }
      }, 1000); // 1 second delay to ensure backend has fully processed the completion
      
    } catch (error) {
      if (isAssessmentPaused(error)) { setPaused(true); setLoading(false); return; }
      console.error('❌ Error completing persona assessment:', error);
      setError(`Failed to complete assessment: ${error.message}`);
    }
  };

  const handlePolicyEvaluationComplete = () => {
    console.log('✅ Policy evaluation completed, offering Risk Optimization Studio access...');
    setShowPolicyEvaluation(false);
    console.log(`${PERSONA_CONFIG.c_level_tech.name} assessment and policy evaluation completed!`);
    
    // Store completion context for studio access
    const assessmentContext = {
      assessmentId: sessionId,
      persona: 'c_level_tech',
      personaDisplayName: PERSONA_CONFIG.c_level_tech.name,
      completedAt: new Date().toISOString(),
      companyName: assessmentSession?.company_profile?.company_name || assessmentSession?.companyName || 'Unknown Company',
      industry: assessmentSession?.company_profile?.industry_vertical || assessmentSession?.industry || 'Unknown'
    };
    sessionStorage.setItem('assessmentContext', JSON.stringify(assessmentContext));
    sessionStorage.setItem('currentPersona', 'c_level_tech');
    
    // Policy evaluation completed - no popup needed
    console.log('🎉 Policy evaluation completed successfully for', PERSONA_CONFIG.c_level_tech.name);
    console.log('📊 Returning to assessment overview');
    setCurrentStep('overview');
    setSelectedPersona(null);
    setCurrentQuestionIndex(0);
    
    // Clear persona parameter from URL
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.delete('persona');
    window.history.replaceState({}, '', currentUrl);
    
    // Refresh data to show updated progress with delay
    console.log('🔄 Refreshing session data after policy evaluation completion...');
    setTimeout(async () => {
      console.log('🔄 Starting delayed refresh after policy evaluation...');
      if (sessionId) {
        // Force a complete refresh of session data
        const sessionResponse = await apiService.get(`/api/foundations/sessions/${sessionId}`);
        if (sessionResponse.success) {
          const freshSessionData = {
            ...sessionResponse.session,
            persona_sessions: sessionResponse.persona_sessions || []
          };
          setAssessmentSession(freshSessionData);
          await loadExistingResponses(freshSessionData);
        }
      } else {
        await loadAssessmentData();
      }
    }, 1000);
  };

  const handlePolicyEvaluationSkip = () => {
    console.log('⏭️ Policy evaluation skipped, returning to overview');
    setShowPolicyEvaluation(false);
    console.log(`${PERSONA_CONFIG.c_level_tech.name} assessment completed! Policy evaluation skipped.`);
    
    // Return to overview and refresh data
    setCurrentStep('overview');
    setSelectedPersona(null);
    setCurrentQuestionIndex(0);
    
    // Clear persona parameter from URL
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.delete('persona');
    window.history.replaceState({}, '', currentUrl);
    
    // Refresh data to show updated progress with delay
    console.log('🔄 Refreshing session data after policy evaluation skip...');
    setTimeout(async () => {
      console.log('🔄 Starting delayed refresh after policy evaluation skip...');
      if (sessionId) {
        // Force a complete refresh of session data
        const sessionResponse = await apiService.get(`/api/foundations/sessions/${sessionId}`);
        if (sessionResponse.success) {
          const freshSessionData = {
            ...sessionResponse.session,
            persona_sessions: sessionResponse.persona_sessions || []
          };
          setAssessmentSession(freshSessionData);
          await loadExistingResponses(freshSessionData);
        }
      } else {
        await loadAssessmentData();
      }
    }, 1000);
  };


  const calculateOverallProgress = () => {
    const totalQuestions = Object.values(progress).reduce((sum, p) => sum + p.total, 0);
    const completedQuestions = Object.values(progress).reduce((sum, p) => sum + p.completed, 0);
    return totalQuestions > 0 ? Math.round((completedQuestions / totalQuestions) * 100) : 0;
  };

  const isAssessmentComplete = () => {
    return Object.values(progress).every(p => p.percentage === 100);
  };

  const startPersonaAssessment = async (personaId) => {
    setSelectedPersona(personaId);
    setCurrentQuestionIndex(0);
    setCurrentStep('assessment');
    
    // Update URL to reflect current persona
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.set('persona', personaId);
    window.history.replaceState({}, '', currentUrl);
    console.log('🔄 Updated URL persona parameter to:', personaId);
    
    // Load existing responses for this persona before showing the assessment
    if (sessionId) {
      console.log('📋 Loading existing responses for persona:', personaId);
      await loadResponseData();
    }
    
    // Pre-fill URL parameters now that persona is selected - delay to ensure questions are loaded
    setTimeout(() => {
      console.log('🔧 Starting pre-fill process for persona:', personaId);
      console.log('🔍 URL params available:', {
        company: window.urlCompanyParam,
        industry: window.urlIndustryParam
      });
      
      // Always pre-fill from URL params if available
      if (window.urlCompanyParam || window.urlIndustryParam) {
        preFillFromUrlParamsWithPersona(personaId);
      }
      
      // Also try companyInfo if URL params not available
      if (!window.urlCompanyParam && companyInfo?.name) {
        autoFillCompanyName(companyInfo.name);
      }
    }, 500); // Increased delay to ensure questions and any existing responses are loaded
  };

  const startEmailSetup = () => {
    setCurrentStep('email-setup');
  };

  const completeEmailSetup = () => {
    setCurrentStep('overview');
  };

  const handleViewResults = () => {
    console.log('🎯 Navigating to results dashboard for session:', sessionId);
    // Navigate to the enhanced results dashboard
    navigate(`/assessment/${sessionId}/results`);
  };

  if (paused) {
    return <AssessmentPausedNotice />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[rgba(255,255,255,0.03)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-[#a8b2d1]">Loading Persona Assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[rgba(255,255,255,0.03)] flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <Card.Body className="text-center">
            <AlertTriangle aria-hidden="true" className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#e6f1ff] mb-2">Error Loading Assessment</h3>
            <p role="alert" className="text-[#a8b2d1] mb-4">{error}</p>
            <Button onClick={loadAssessmentData} variant="primary">
              Try Again
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Policy Evaluation Page - Show after C-Level Tech completion
  if (showPolicyEvaluation) {
    return (
      <PolicyEvaluationPage
        sessionId={sessionId}
        onComplete={handlePolicyEvaluationComplete}
        onSkip={handlePolicyEvaluationSkip}
      />
    );
  }

  // Locked-persona thank-you state — end-of-flow setCurrentStep('overview')
  // calls would otherwise drop the persona on the multi-persona switcher,
  // which doesn't make sense when they're scoped to a single section.
  if (currentStep === 'overview' && lockedPersona) {
    const personaCfg = PERSONA_CONFIG[lockedPersona] || { name: lockedPersona };
    return (
      <div className="min-h-screen bg-[rgba(255,255,255,0.03)] flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <Card.Body className="text-center">
            <CheckCircle aria-hidden="true" className="h-16 w-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#e6f1ff] mb-2">Thank you</h3>
            <p className="text-[#a8b2d1] mb-4">
              Your responses for the {personaCfg.name} section have been recorded. You can safely close this window.
            </p>
            <Button onClick={signOutPersona} variant="primary">
              Sign Out
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Overview Step - Persona Selection
  if (currentStep === 'overview') {
    return (
      <div className="min-h-screen bg-[rgba(255,255,255,0.03)]">
        {/* Header */}
        <div className="ra-glass shadow-soft border-b border-[rgba(255,255,255,0.12)]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button onClick={onBack} className="inline-flex items-center px-3 py-2 text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors">
                  <ChevronLeft aria-hidden="true" className="w-5 h-5 mr-1" />
                  Back
                </button>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-xl gradient-blue flex items-center justify-center text-white">
                    <Users aria-hidden="true" className="w-6 h-6" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-[#e6f1ff]">Persona-Based Assessment</h1>
                    <p className="text-sm text-[#a8b2d1]">
                      {companyInfo?.name || 'Company'} • Multi-User Evaluation
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600">{calculateOverallProgress()}%</div>
                  <div className="text-xs text-[#a8b2d1]">Complete</div>
                </div>
                <button onClick={startEmailSetup} className="inline-flex items-center px-4 py-2 ra-glass text-[#ccd6f6] font-medium border border-[rgba(255,255,255,0.18)] rounded-lg hover:bg-[rgba(255,255,255,0.03)] transition-smooth shadow-soft">
                  <Send aria-hidden="true" className="w-4 h-4 mr-2" />
                  Setup Emails
                </button>
                {isAssessmentComplete() && (
                  <button onClick={handleViewResults} className="inline-flex items-center px-4 py-2 gradient-blue text-white font-medium rounded-lg hover:shadow-lifted transition-smooth">
                    <CheckCircle aria-hidden="true" className="w-4 h-4 mr-2" />
                    View Results
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* How It Works Guide */}
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <HowItWorksGuide
            moduleName="Persona Assessment"
            steps={[
              { title: 'Select a Persona', desc: 'Choose which role to assess — CEO, CISO, CFO, HR Director, or Legal Counsel. Each persona sees the same 51 questions but answers from their unique organizational perspective.' },
              { title: 'Answer Questions', desc: 'Rate each question on a 1-5 scale based on your organization\'s current practices. Be honest — the value comes from revealing gaps, not achieving a high score. Each question maps to one of the Big 6 risk categories.' },
              { title: 'Track Progress', desc: 'Monitor completion across all personas. The progress bar shows how many questions each persona has answered. You can pause and resume at any time — answers are saved automatically.' },
              { title: 'Compare Perspectives', desc: 'As multiple personas complete their assessments, the system identifies divergence — where the CEO says "we\'re strong" but the CISO says "we\'re weak." These gaps are the most actionable findings.' },
            ]}
            footnote="Send assessment links via the Setup Emails button. Each persona gets a unique link — no account required. Results appear on the Assessment Dashboard as responses come in."
          />
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Progress Overview Card */}
          <div className="ra-glass rounded-xl shadow-soft p-6 mb-6" aria-live="polite">
            <div className="flex items-center mb-6">
              <BarChart3 aria-hidden="true" className="w-6 h-6 text-primary-600 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-[#e6f1ff]">Assessment Progress Overview</h2>
                <p className="text-sm text-[#a8b2d1]">
                  Track completion across all personas and view overall progress
                </p>
              </div>
            </div>
            
            {/* Overall Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#ccd6f6]">Overall Progress</span>
                <span className="text-sm font-semibold text-primary-600">{calculateOverallProgress()}% Complete</span>
              </div>
              <div className="w-full bg-[#1e3148] rounded-full h-3">
                <div 
                  className="gradient-blue h-3 rounded-full transition-all duration-500"
                  style={{ width: `${calculateOverallProgress()}%` }}
                />
              </div>
            </div>
            
            {/* Individual Persona Progress */}
            <div className="grid grid-cols-4 gap-4">
              {Object.entries(progress).map(([personaId, prog]) => (
                <div key={personaId} className="text-center">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-2 ${
                    PERSONA_CONFIG[personaId].color === 'blue' ? 'gradient-blue' :
                    PERSONA_CONFIG[personaId].color === 'green' ? 'gradient-success' :
                    PERSONA_CONFIG[personaId].color === 'purple' ? 'gradient-purple' :
                    'gradient-warning'
                  } text-white ${prog.percentage === 100 ? 'ring-4 ring-green-200' : ''}`}>
                    {PERSONA_CONFIG[personaId].icon}
                  </div>
                  <p className="text-sm font-medium text-[#e6f1ff]">
                    {PERSONA_CONFIG[personaId].name.split(' ')[0]}
                  </p>
                  <p className={`text-lg font-bold ${prog.percentage === 100 ? 'text-green-600' : 'text-[#a8b2d1]'}`}>{prog.percentage}%</p>
                </div>
              ))}
            </div>
          </div>

          {/* Persona Cards - 2x2 Grid */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            {Object.entries(PERSONA_CONFIG).map(([personaId, persona]) => {
              const personaProgress = progress[personaId] || { total: 0, completed: 0, percentage: 0 };
              const isComplete = personaProgress.percentage === 100;
              
              return (
                <div 
                  key={personaId} 
                  className="ra-glass rounded-xl shadow-soft border border-[rgba(255,255,255,0.12)] p-6 hover:shadow-elevated transition-smooth"
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${
                        persona.color === 'blue' ? 'gradient-blue' :
                        persona.color === 'green' ? 'gradient-success' :
                        persona.color === 'purple' ? 'gradient-purple' :
                        'gradient-warning'
                      }`}>
                        {persona.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#e6f1ff]">{persona.name}</h3>
                        <p className="text-sm text-[#a8b2d1]">{persona.description}</p>
                      </div>
                    </div>
                    {isComplete && <CheckCircle aria-hidden="true" className="w-6 h-6 text-green-600 flex-shrink-0" />}
                  </div>
                  
                  {/* Progress Stats */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-[#a8b2d1]">
                      {personaProgress.completed} of {personaProgress.total} questions
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      isComplete ? 'bg-green-100 text-[#ccd6f6]' : 
                      personaProgress.completed > 0 ? 'bg-orange-100 text-[#ccd6f6]' : 
                      'bg-[#172a45] text-[#e6f1ff]'
                    }`}>
                      {personaProgress.percentage}%
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-[#1e3148] rounded-full h-2 mb-4">
                    <div 
                      className={`h-2 rounded-full transition-smooth ${
                        isComplete ? 'gradient-success' : 
                        personaProgress.completed > 0 ? 'gradient-warning' : 
                        'bg-[rgba(255,255,255,0.12)]'
                      }`}
                      style={{ width: `${personaProgress.percentage}%` }}
                    />
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <button
                      className={`w-full inline-flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-smooth ${
                        isComplete 
                          ? 'gradient-success text-white hover:shadow-lifted' 
                          : 'gradient-blue text-white hover:shadow-lifted'
                      }`}
                      onClick={() => startPersonaAssessment(personaId)}
                    >
                      {isComplete ? (
                        <>
                          <Eye aria-hidden="true" className="w-4 h-4 mr-2" />
                          Review Answers
                        </>
                      ) : (
                        <>
                          <ChevronRight aria-hidden="true" className="w-4 h-4 mr-2" />
                          {personaProgress.completed > 0 ? 'Continue' : 'Start Assessment'}
                        </>
                      )}
                    </button>
                    
                    {personaProgress.completed > 0 && !isComplete && (
                      <button
                        className="w-full inline-flex items-center justify-center px-4 py-2 ra-glass text-[#ccd6f6] font-medium border border-[rgba(255,255,255,0.18)] rounded-lg hover:bg-[rgba(255,255,255,0.03)] transition-smooth"
                        onClick={() => saveResponses()}
                        disabled={saving}
                      >
                        <Save aria-hidden="true" className="w-4 h-4 mr-2" />
                        <span role={saving ? "status" : undefined} aria-live={saving ? "polite" : undefined}>{saving ? 'Saving...' : 'Save Progress'}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Instructions */}
          <Card className="mt-8">
            <Card.Header>
              <Card.Title icon={<Target />}>Assessment Instructions</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-[#e6f1ff] mb-3">How it Works</h4>
                  <ul className="space-y-2 text-sm text-[#a8b2d1]">
                    <li className="flex items-center space-x-2">
                      <CheckCircle aria-hidden="true" className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Each persona answers specific questions assigned to their role</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle aria-hidden="true" className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Progress is saved automatically as you complete each section</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle aria-hidden="true" className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Responses are averaged to create comprehensive risk scores</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle aria-hidden="true" className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Complete all personas to unlock the full assessment results</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#e6f1ff] mb-3">Assessment Features</h4>
                  <ul className="space-y-2 text-sm text-[#a8b2d1]">
                    <li className="flex items-center space-x-2">
                      <Users aria-hidden="true" className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>Multi-user collaborative assessment</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Target aria-hidden="true" className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>Role-specific question filtering</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Activity aria-hidden="true" className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>Real-time progress tracking</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Award className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>360-degree analytics and insights</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  // Email Setup Step - the one real invitation path (setup-personas, then send-persona-invitations).
  // This used to render PersonaEmailSetup, a mock that waited a second and discarded the addresses.
  if (currentStep === 'email-setup') {
    return (
      <PersonaSetup
        sessionId={sessionId}
        companyName={companyInfo?.name}
        onComplete={completeEmailSetup}
        onCancel={() => setCurrentStep('overview')}
      />
    );
  }

  // Assessment Step - Category-based Interface
  if (currentStep === 'assessment' && selectedPersona) {
    const personaQuestions = getQuestionsForPersona(selectedPersona);
    const persona = PERSONA_CONFIG[selectedPersona];

    // Group questions by category
    const questionsByCategory = {
      company_profile: personaQuestions.filter(q => q.category === 'company_profile'),
      e360: personaQuestions.filter(q => q.category === 'e360'),
      d360: personaQuestions.filter(q => q.category === 'd360'),
      c360: personaQuestions.filter(q => q.category === 'c360'),
      p360: personaQuestions.filter(q => q.category === 'p360'),
      cyber_insurance: personaQuestions.filter(q => q.category === 'cyber_insurance')
    };

    // Define category order and names - only include categories with questions
    const allCategories = [
      { key: 'company_profile', name: 'Company Profile', description: 'Basic company information' },
      { key: 'cyber_insurance', name: 'Cyber Insurance', description: 'Cyber insurance coverage details' },
      { key: 'e360', name: 'Enterprise 360°', description: 'Enterprise and organizational assessment' },
      { key: 'd360', name: 'Data 360°', description: 'Data management and governance' },
      { key: 'c360', name: 'Cyber 360°', description: 'Cybersecurity posture assessment' },
      { key: 'p360', name: 'People 360°', description: 'People and process evaluation' }
    ];
    
    // Filter out categories with no questions for this persona
    const categories = allCategories.filter(cat => {
      const categoryQuestions = questionsByCategory[cat.key];
      return categoryQuestions && categoryQuestions.length > 0;
    });

    // Determine current category based on currentQuestionIndex
    let currentCategoryIndex = 0;
    let questionsPassedSoFar = 0;
    
    for (let i = 0; i < categories.length; i++) {
      const categoryQuestions = questionsByCategory[categories[i].key];
      if (currentQuestionIndex < questionsPassedSoFar + categoryQuestions.length) {
        currentCategoryIndex = i;
        break;
      }
      questionsPassedSoFar += categoryQuestions.length;
    }

    const currentCategory = categories[currentCategoryIndex];
    const currentCategoryQuestions = questionsByCategory[currentCategory.key];

    // Render the current category page - Professional Insights styling
    return (
      <div className="insights-dashboard">
        <div className="insights-assessment-container">
          <div className="insights-assessment-card">
            {/* Assessment Header */}
            <div className="insights-assessment-header">
              <div className="insights-assessment-header-content">
                <div className="insights-assessment-header-info">
                  <div className="insights-assessment-icon">
                    {persona.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="insights-assessment-title">{currentCategory.name}</h1>
                      {getCategoryHelp(currentCategory.key) && (
                        <HelpTooltip
                          title={getCategoryHelp(currentCategory.key).title}
                          content={getCategoryHelp(currentCategory.key).blurb}
                          iconSize="md"
                          side="bottom"
                        />
                      )}
                    </div>
                    <p className="insights-assessment-subtitle">{currentCategory.description}</p>
                    <p className="insights-assessment-persona">Persona: {persona.name}</p>
                  </div>
                </div>
                <div className="insights-assessment-progress" aria-live="polite">
                  <span className="insights-assessment-step">
                    {currentCategoryIndex + 1}/{categories.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Questions Container */}
            <div className="insights-assessment-body">
              <div className="insights-questions-list">
                {(() => {
                  return null;
                })()}
                {currentCategoryQuestions.map((question, index) => {
                  return (
                  <div key={question.id} className="insights-question-item">
                    <label className="insights-question-label">
                      <span className="insights-question-text">{question.question_text}</span>
                    </label>
                    
                    <div className="insights-question-input">
                      {((question.question_type === 'select' && (question.options || question.descriptions)) || (question.question_type === 'scale' && question.descriptions) || (question.question_type === 'boolean' && question.descriptions)) && (
                        <>
                          {/* Debug ALL 360 rendering paths */}
                          
                          {/* For 360 questions with descriptions, show clickable scoring options */}
                          {(() => {
                            return null;
                          })()}
                          {question.descriptions && question.scale ? (
                            <div className="space-y-3">
                              <div className="text-sm font-medium text-[#ccd6f6] mb-3">
                                Select the response that best describes your organization:
                              </div>
                              {Object.entries(question.descriptions).map(([score, description]) => (
                                <div 
                                  key={score} 
                                  onClick={() => handleResponseChange(question.id, score, selectedPersona)}
                                  className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                                    responses[`${question.id}_${selectedPersona}`]?.response_value === score
                                      ? 'border-blue-500 bg-blue-50 shadow-[0_4px_24px_rgba(0,0,0,0.3)]' 
                                      : 'border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)] hover:border-blue-300 hover:bg-blue-25'
                                  }`}
                                >
                                  <div className="font-medium text-blue-600 mb-1">Score {score}</div>
                                  <div className="text-[#ccd6f6]">{description}</div>
                                  {responses[`${question.id}_${selectedPersona}`]?.response_value === score && (
                                    <div className="flex items-center mt-2 text-blue-600 text-sm font-medium">
                                      <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                                      Selected
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            /* For other select questions without descriptions, use dropdown */
                            <select
                              className="insights-form-select"
                              aria-label={question.question_text}
                              value={getResponseValue(question.id, selectedPersona)}
                              onChange={(e) => {
                                handleResponseChange(question.id, e.target.value, selectedPersona);
                              }}
                            >
                              <option value="">Select...</option>
                              {question.options.map((option, i) => (
                                <option key={i} value={option}>
                                  {question.scale ? `${option}` : option}
                                </option>
                              ))}
                            </select>
                          )}
                        </>
                      )}
                      {question.question_type === 'text' && (
                        <input
                          type="text"
                          className="insights-form-input"
                          aria-label={question.question_text}
                          value={getResponseValue(question.id, selectedPersona)}
                          onChange={(e) => handleResponseChange(question.id, e.target.value, selectedPersona)}
                          placeholder="Enter your answer..."
                        />
                      )}
                      {(question.question_type === 'number' || question.question_type === 'numeric') && (
                        <input
                          type="number"
                          min="0"
                          className="insights-form-input"
                          aria-label={question.question_text}
                          value={getResponseValue(question.id, selectedPersona)}
                          onChange={(e) => handleResponseChange(question.id, e.target.value, selectedPersona)}
                          onWheel={(e) => e.currentTarget.blur()}
                          placeholder="Enter number..."
                        />
                      )}
                      {question.question_type === 'currency' && (
                        <div className="insights-currency-input">
                          <span className="insights-currency-symbol">$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="insights-form-input"
                            aria-label={question.question_text}
                            value={(() => {
                              const raw = getResponseValue(question.id, selectedPersona);
                              if (raw === '' || raw == null) return '';
                              const num = parseFloat(raw);
                              if (isNaN(num)) return raw;
                              // Format with commas but preserve decimals the user typed
                              const parts = String(raw).split('.');
                              parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                              return parts.join('.');
                            })()}
                            onChange={(e) => {
                              // Strip commas, allow digits and decimal point only
                              const stripped = e.target.value.replace(/,/g, '');
                              if (stripped === '' || /^\d*\.?\d*$/.test(stripped)) {
                                handleResponseChange(question.id, stripped, selectedPersona);
                              }
                            }}
                            placeholder="0.00"
                          />
                          <span className="insights-currency-hint" style={{ fontSize: '11px', color: 'var(--ra-text-muted)', marginLeft: '8px' }}>
                            in millions (e.g. 15.0 = $15M)
                          </span>
                        </div>
                      )}
                      {question.question_type === 'percentage' && (
                        <div className="insights-percentage-input">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            className="insights-form-input"
                            aria-label={question.question_text}
                            placeholder="0-100"
                            value={getResponseValue(question.id, selectedPersona)}
                            onChange={(e) => handleResponseChange(question.id, e.target.value, selectedPersona)}
                          />
                          <span className="insights-percentage-symbol">%</span>
                        </div>
                      )}
                      {question.question_type === 'slider' && (
                        <>
                          <div className="insights-slider-container">
                            <input
                              type="range"
                              min="0"
                              max="5"
                              step="1"
                              className="insights-slider"
                              aria-label={question.question_text}
                              value={getResponseValue(question.id, selectedPersona) || '0'}
                              onChange={(e) => handleResponseChange(question.id, e.target.value, selectedPersona)}
                            />
                            <div className="insights-slider-labels">
                              <span>0</span>
                              <span>1</span>
                              <span>2</span>
                              <span>3</span>
                              <span>4</span>
                              <span>5</span>
                            </div>
                            <div className="insights-slider-value">
                              Current: {getResponseValue(question.id, selectedPersona) || '0'}
                            </div>
                          </div>
                          
                          {/* Insights-style scoring guide toggle */}
                          {question.descriptions && question.scale && (
                            <div style={{ marginTop: '1rem' }}>
                              <button
                                type="button"
                                onClick={() => toggleScoringGuide(question.id)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  color: 'var(--ra-cyber-aqua)',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  transition: 'color 0.15s'
                                }}
                                onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
                                onMouseOut={(e) => e.target.style.color = '#2563eb'}
                              >
                                <Info aria-hidden="true" className="w-4 h-4" style={{ marginRight: '0.25rem' }} />
                                {scoringGuideVisible[question.id] ? 'Hide scoring guide' : 'Show scoring guide'}
                              </button>
                              
                              {scoringGuideVisible[question.id] && (
                                <div style={{
                                  marginTop: '0.75rem',
                                  padding: '1rem',
                                  backgroundColor: '#eff6ff',
                                  borderRadius: '0.5rem',
                                  border: '1px solid #bfdbfe'
                                }}>
                                  <h4 style={{
                                    fontWeight: '500',
                                    color: '#1e3a8a',
                                    marginBottom: '0.75rem'
                                  }}>Scoring Guide:</h4>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {Object.entries(question.descriptions).map(([score, description]) => (
                                      <div 
                                        key={score} 
                                        style={{
                                          padding: '0.75rem',
                                          borderRadius: '0.25rem',
                                          border: '1px solid',
                                          fontSize: '0.875rem',
                                          borderColor: responses[`${question.id}_${selectedPersona}`]?.response_value === score
                                            ? '#3b82f6' 
                                            : '#e5e7eb',
                                          backgroundColor: responses[`${question.id}_${selectedPersona}`]?.response_value === score
                                            ? '#dbeafe' 
                                            : 'white'
                                        }}
                                      >
                                        <div style={{
                                          fontWeight: '500',
                                          color: '#0066ff'
                                        }}>{score} - {description}</div>
                                      </div>
                                    ))}
                                  </div>
                                  <div style={{
                                    marginTop: '0.75rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '0.875rem',
                                    color: '#0066ff'
                                  }}>
                                    <span style={{ marginRight: '0.25rem' }}>💡</span>
                                    Your current selection ({responses[`${question.id}_${selectedPersona}`]?.response_value || '0'}) is highlighted above
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Debug and fallback for questions without recognized types */}
                      {(() => {
                        // Check if this question already has a proper input rendered above
                        const hasProperInput = (
                          (question.question_type === 'select' && (question.options || question.descriptions)) ||
                          (question.question_type === 'scale' && question.descriptions) ||
                          // Handle 360 questions that might have descriptions regardless of type
                          (question.descriptions && Object.keys(question.descriptions).length > 0) ||
                          question.question_type === 'text' ||
                          question.question_type === 'number' ||
                          question.question_type === 'numeric' ||
                          question.question_type === 'currency' ||
                          question.question_type === 'percentage' ||
                          question.question_type === 'slider'
                        );
                        
                        // Show fallback for truly unrecognized types
                        if (!hasProperInput) {
                          console.warn(`⚠️ Question ${question.id} has no valid input type, adding fallback text input`);
                          return (
                            <div>
                              <div className="text-xs text-orange-600 mb-2">
                                ⚠️ Fallback input (question_type: {question.question_type || 'undefined'})
                              </div>
                              <input
                                type="text"
                                className="insights-form-input"
                                aria-label={question.question_text || 'Enter your answer'}
                                value={getResponseValue(question.id, selectedPersona)}
                                onChange={(e) => handleResponseChange(question.id, e.target.value, selectedPersona)}
                                placeholder="Enter your answer..."
                              />
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Assessment Footer */}
            <div className="insights-assessment-footer">
              {currentCategoryIndex > 0 ? (
                <button
                  onClick={() => {
                    saveResponses(selectedPersona);
                    // Navigate to previous category
                    let prevCategoryStartIndex = 0;
                    for (let i = 0; i < currentCategoryIndex - 1; i++) {
                      prevCategoryStartIndex += questionsByCategory[categories[i].key].length;
                    }
                    setCurrentQuestionIndex(prevCategoryStartIndex);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="insights-btn-secondary"
                >
                  <ArrowLeft aria-hidden="true" className="w-4 h-4" />
                  Previous Section
                </button>
              ) : lockedPersona ? (
                <button
                  onClick={signOutPersona}
                  className="insights-btn-secondary"
                >
                  <ArrowLeft aria-hidden="true" className="w-4 h-4" />
                  Sign Out
                </button>
              ) : (
                <button
                  onClick={() => {
                    setCurrentStep('overview');
                    const currentUrl = new URL(window.location);
                    currentUrl.searchParams.delete('persona');
                    window.history.replaceState({}, '', currentUrl);
                  }}
                  className="insights-btn-secondary"
                >
                  <ArrowLeft aria-hidden="true" className="w-4 h-4" />
                  Back to Overview
                </button>
              )}
              <button
                onClick={() => {
                  // Save current responses
                  saveResponses(selectedPersona);
                  // Go to next category or complete
                  if (currentCategoryIndex < categories.length - 1) {
                    // Calculate the starting index of the next category
                    let nextCategoryStartIndex = 0;
                    for (let i = 0; i <= currentCategoryIndex; i++) {
                      nextCategoryStartIndex += questionsByCategory[categories[i].key].length;
                    }
                    setCurrentQuestionIndex(nextCategoryStartIndex);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    completePersonaAssessment();
                  }
                }}
                className="insights-btn-primary"
              >
                {currentCategoryIndex < categories.length - 1 ? (
                  <>
                    Next Section
                    <ChevronRight aria-hidden="true" className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Complete Assessment
                    <CheckCircle aria-hidden="true" className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

    // Define current question and response for single-question view
    const currentQuestion = personaQuestions[currentQuestionIndex];
    const currentResponse = responses[`${currentQuestion?.id}_${selectedPersona}`]?.response_value || '';

    if (!currentQuestion) {
      return (
        <div className="min-h-screen bg-[rgba(255,255,255,0.03)] flex items-center justify-center">
          <Card className="max-w-md w-full mx-4">
            <Card.Body className="text-center">
              <AlertTriangle aria-hidden="true" className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#e6f1ff] mb-2">No Questions Available</h3>
              <p className="text-[#a8b2d1] mb-4">
                No questions found for {persona.name}. This may indicate a data loading issue.
              </p>
              <Button onClick={() => {
                if (lockedPersona) {
                  signOutPersona();
                  return;
                }
                setCurrentStep('overview');
                // Clear persona parameter from URL
                const currentUrl = new URL(window.location);
                currentUrl.searchParams.delete('persona');
                window.history.replaceState({}, '', currentUrl);
              }} variant="primary">
                {lockedPersona ? 'Sign Out' : 'Back to Overview'}
              </Button>
            </Card.Body>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[rgba(255,255,255,0.03)]">
        {/* Assessment Header */}
        <div className="ra-glass border-b border-[rgba(255,255,255,0.12)]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (lockedPersona) {
                      signOutPersona();
                      return;
                    }
                    setCurrentStep('overview');
                    // Clear persona parameter from URL
                    const currentUrl = new URL(window.location);
                    currentUrl.searchParams.delete('persona');
                    window.history.replaceState({}, '', currentUrl);
                  }}
                  icon={<ChevronLeft />}
                >
                  {lockedPersona ? 'Sign Out' : 'Back to Overview'}
                </Button>
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg bg-${persona.color}-100 text-${persona.color}-600`}>
                    {persona.icon}
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-[#e6f1ff]">{persona.name}</h1>
                    <p className="text-sm text-[#a8b2d1]">
                      {currentQuestionIndex + 1} of {personaQuestions.length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Badge variant="primary">
                  {Math.round(((currentQuestionIndex + 1) / personaQuestions.length) * 100)}% Complete
                </Badge>
                <Button
                  variant="ghost"
                  onClick={saveResponses}
                  disabled={saving}
                  icon={<Save />}
                >
                  <span role={saving ? "status" : undefined} aria-live={saving ? "polite" : undefined}>{saving ? 'Saving...' : 'Save'}</span>
                </Button>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="pb-4">
              <div className="w-full bg-[#1e3148] rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-smooth"
                  style={{ width: `${((currentQuestionIndex + 1) / personaQuestions.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Question Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <Card.Header>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <Card.Title>{currentQuestion.question_text}</Card.Title>
                  {currentQuestion.description && (
                    <Card.Description className="mt-2">
                      {currentQuestion.description}
                    </Card.Description>
                  )}
                </div>
                {currentQuestion.category && (
                  <Badge variant="info" size="sm">
                    {currentQuestion.category}
                  </Badge>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {/* Question Type: Multiple Choice / Select */}
              {(currentQuestion.question_type === 'multiple_choice' || currentQuestion.question_type === 'select') && currentQuestion.options && (
                <div className="space-y-3">
                  {currentQuestion.options.map((option, index) => (
                    <label 
                      key={index}
                      className="flex items-center p-4 border border-[rgba(255,255,255,0.12)] rounded-lg cursor-pointer hover:bg-[rgba(255,255,255,0.03)] transition-colors"
                    >
                      <input
                        type="radio"
                        name={`question_${currentQuestion.id}`}
                        value={option.value || option}
                        checked={currentResponse === (option.value || option)}
                        onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value, selectedPersona)}
                        aria-label={option.label || option}
                        className="h-4 w-4 text-blue-600 border-[rgba(255,255,255,0.18)] focus:ring-[rgba(0,217,255,0.5)]"
                      />
                      <span className="ml-3 text-[#e6f1ff]">{option.label || option}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Question Type: Scale (1-5) */}
              {currentQuestion.question_type === 'scale' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-sm text-[#a8b2d1]">
                    <span>Strongly Disagree</span>
                    <span>Strongly Agree</span>
                  </div>
                  <div className="flex justify-center space-x-4">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <label key={value} className="flex flex-col items-center cursor-pointer">
                        <input
                          type="radio"
                          name={`question_${currentQuestion.id}`}
                          value={value}
                          checked={currentResponse === value.toString()}
                          onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value, selectedPersona)}
                          aria-label={`Scale ${value} of 5`}
                          className="h-5 w-5 text-blue-600 border-[rgba(255,255,255,0.18)] focus:ring-[rgba(0,217,255,0.5)] mb-2"
                        />
                        <span className="text-sm font-medium text-[#ccd6f6]">{value}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Question Type: Text Input */}
              {currentQuestion.question_type === 'text' && (
                <textarea
                  value={currentResponse}
                  onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value, selectedPersona)}
                  placeholder="Enter your response..."
                  aria-label={currentQuestion.question_text}
                  className="w-full p-4 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)] focus:border-[#00d9ff] resize-none"
                  rows="4"
                />
              )}

              {/* Question Type: Number Input */}
              {currentQuestion.question_type === 'number' && (
                <input
                  type="number"
                  value={currentResponse}
                  onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value, selectedPersona)}
                  placeholder="Enter a number..."
                  aria-label={currentQuestion.question_text}
                  className="w-full p-4 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)] focus:border-[#00d9ff]"
                />
              )}
            </Card.Body>
            <Card.Footer>
              <div className="flex justify-between items-center">
                <Button
                  variant="ghost"
                  onClick={previousQuestion}
                  disabled={currentQuestionIndex === 0}
                  icon={<ChevronLeft />}
                >
                  Previous
                </Button>
                
                <div className="flex space-x-3">
                  {isLastQuestion ? (
                    <PrimaryButton
                      onClick={completePersonaAssessment}
                      disabled={!currentResponse}
                      icon={<CheckCircle />}
                    >
                      Complete {persona.name} Assessment
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton
                      onClick={nextQuestion}
                      disabled={!currentResponse}
                      icon={<ChevronRight />}
                    >
                      Next Question
                    </PrimaryButton>
                  )}
                </div>
              </div>
            </Card.Footer>
          </Card>

          {/* Additional Context Card */}
          {currentQuestion.context_info && (
            <Card className="mt-6">
              <Card.Header>
                <Card.Title icon={<FileText />} className="text-sm">
                  Additional Context
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <p className="text-sm text-[#a8b2d1]">{currentQuestion.context_info}</p>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default PersonaAssessmentFlow;